import Text from 'components/shared/Text'
import Section from 'components/shared/Section'
import SwitchDays from 'components/shared/SwitchDays'
import { FormattedMessage, useIntl } from 'react-intl'
import NumberPicker from 'components/shared/NumberPicker'
import Datepicker from 'components/shared/Datepicker'
import Box from 'components/shared/Box'
import Button from 'components/shared/Button'
import CalendarModal from 'components/global/CalendarModal'
import { useAppSelector, useAppDispatch } from 'store/hooks'
import {
    addPerson,
    removeAllTickets,
    setIsCalendarOpened,
    setMultipleDays,
    setTicketsVisible,
} from 'store/ticket-slice'
import OpeningHoursModal from 'components/global/OpeningHoursModal'
import { useAutoScroll } from 'utils/hooks'
import { useState, useEffect, useMemo, useRef } from 'react'
import { TICKETS_TO_PARK_LIMIT } from 'constants/limits'
import { removeTicket } from 'store/ticket-slice/index'
import { useTickets } from 'store/ticket-slice/hooks'
import { scrollTo } from 'helpers/animateScroll'
import { useResolutions } from 'store/resolutions'
import TicketsWithAccommodationDiscountInfo from 'components/shared/Promotions/components/TicketsWithAccommodationDiscountInfo'
import { useCart } from 'store/cart-slice/hooks'
import { pushDataLayer } from 'helpers/helpers'
import { useApp } from 'store/steps'
import { MaintenanceBox } from 'components/shared/MaintenanceBox'
import { useListTicketAvailableDaysQuery } from 'store/api-slice/hooks'
import { useSearchParams } from 'react-router-dom'
import { FamilyCheckbox } from 'components/global/Family/components/FamilyCheckbox'
import { useFamilyCheckbox } from 'components/global/Family/components/FamilyCheckbox/useFamilyCheckbox'
import { FamilyLoader } from 'components/global/Family/components/FamilyLoader'
import { FAMILY_PACKAGE_PERSON_COUNT } from 'components/global/Family/constants'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'
import { DefaultInfo } from './DefaultInfo'
import { useTicketPromotions } from 'routes/Tickets/utils/useTicketPromotions'
import { getDarkModeByLanguage } from 'helpers/theme'

const FindTickets = () => {
    const { isTablet } = useResolutions()
    const [searchParams] = useSearchParams()
    const { language, isCreatorAccommodation } = useApp()
    const { isDarkThemeForTickets } = usePromotions()
    const dateFieldRef = useRef<HTMLDivElement>(null)
    const dispatch = useAppDispatch()
    const { formatMessage } = useIntl()
    const { isStepEmpty, isCartFetching } = useCart()
    const { calculateScroll, animatePendingScroll, scrollIndex } =
        useAutoScroll()
    const {
        data: rawFamilyData,
        reducedData: reducedFamilyData,
        isDisabled: isDisabledFamilyPackage,
        isVisible: isFamilyPackageVisible,
        isSelected: isFamilyPackageSelected,
        isDataFetching: isFamilyPackageDataFetching,
        isDataComplete: isFamilyDataComplete,
        handleCheckboxSelection: handleSpecialOfferSelection,
    } = useFamilyCheckbox()
    const { renderActivePromotions } = useTicketPromotions()

    const { people, isMultipleDays, dates, ticketBoxes, areTicketsUpdating } =
        useAppSelector((state) => state.ticket)
    const lastTicketId = ticketBoxes[ticketBoxes?.length - 1]?.boxId
    const {
        removeTicketFromCart,
        isFamilyPackageProcessing,
        hasFamilyPackageSucceeded,
        addFamilyPackageToCart,
        removeAllTicketsFromCart,
        isTicketProcessing,
        isFamilyPackageInCart,
        removeFamilyPackageFromCart,
    } = useTickets(lastTicketId)

    const { tickets, accommodation } = useAppSelector(
        (state) => state.cart.items
    )

    const { isCalendarOpened } = useAppSelector((state) => state.ticket)

    const [isDifferentHoursOpened, setIsDifferentHoursOpened] = useState(false)

    const hasPrefedinedUrlParams =
        !!searchParams.get('singleDay') || !!searchParams.get('dateRange')

    const theme = isDarkThemeForTickets
        ? getDarkModeByLanguage(language)
        : undefined
    const labelColor = isDarkThemeForTickets ? 'grayscale_0' : 'grayscale_900'

    const { detail } = accommodation || {}
    const accommodationGuestsCount =
        detail?.specialRequests?.guests?.length || 0

    const isSpecialOfferUnselected =
        isFamilyPackageVisible && !isFamilyPackageSelected
    const isContinueDisabled = dates.length === 0 || isFamilyPackageDataFetching

    const { data } = useListTicketAvailableDaysQuery(
        {
            _locale: language,
        },
        { skip: !isCalendarOpened && !hasPrefedinedUrlParams }
    )
    const matchingEvent = useMemo(() => {
        const events = data?.events || []
        const matchedEvent = events.find(({ dates: eventDates }) =>
            eventDates?.some((date) => dates.includes(date))
        )
        return matchedEvent
    }, [data, dates])
    const isMaintenanceInfoVisible = !!matchingEvent

    const handleAddPerson = () => {
        if (areTicketsUpdating) return

        dispatch(addPerson())

        calculateScroll({
            trigerringSection: 'tickets',
            trigerringAction: 'add',
        })
    }

    const handleRemovePerson = () => {
        if (areTicketsUpdating) return
        if (tickets.length > 0) {
            removeTicketFromCart().then(() =>
                dispatch(removeTicket(lastTicketId))
            )
        } else {
            dispatch(removeTicket(lastTicketId))
        }
    }

    const handleCheckboxChangeWithEffects = () => {
        handleSpecialOfferSelection({
            isSelected: !isFamilyPackageSelected,
            willAvoidAction: false,
            shouldKeepTickets: true,
            peopleCount:
                isFamilyPackageSelected && !isFamilyPackageInCart
                    ? ticketBoxes.length
                    : undefined,
        })
    }

    const handleShowTickets = async () => {
        if (!isFamilyPackageSelected && !isFamilyPackageInCart) {
            await removeAllTicketsFromCart(true)
            handleSpecialOfferSelection({
                isSelected: false,
                peopleCount: people,
                shouldKeepTickets: false,
            })
            doShowTicketsActions()
            return
        }

        if (!isFamilyPackageSelected && isFamilyPackageInCart) {
            await removeFamilyPackageFromCart()
            handleSpecialOfferSelection({
                isSelected: false,
                peopleCount: people,
                shouldKeepTickets: !!tickets.length,
            })
            doShowTicketsActions()
            return
        }

        if (isFamilyPackageSelected && !isFamilyPackageInCart) {
            try {
                await removeAllTicketsFromCart(true)
                await addFamilyPackageToCart(rawFamilyData)
                handleSpecialOfferSelection({
                    isSelected: true,
                    peopleCount: people,
                })
                doShowTicketsActions(true)
            } catch (error) {
                console.log(error)
            }
        }

        if (isFamilyPackageSelected && isFamilyPackageInCart) {
            try {
                const isResp = await removeAllTicketsFromCart(true)
                if (isResp) {
                    await addFamilyPackageToCart(rawFamilyData)
                    handleSpecialOfferSelection({
                        isSelected: true,
                        peopleCount: people,
                    })
                    doShowTicketsActions(true)
                }
            } catch (error) {
                console.log(error)
            }
        }
    }

    const toggleMultipleDays = () => {
        if (areTicketsUpdating) return
        // handleSpecialOfferVisibility(false)
        removeAllTicketsFromCart().then(() => {
            dispatch(setMultipleDays(!isMultipleDays))
        })
    }

    const doShowTicketsActions = (withSpecialOffer = false) => {
        dispatch(setTicketsVisible(true))

        calculateScroll({
            trigerringSection: 'tickets',
            trigerringAction: withSpecialOffer ? 'show_with_offer' : 'show',
        })

        // < analytics
        !isCreatorAccommodation &&
            pushDataLayer({
                event: 'funnelStep',
                event_category: 'tickets',
                event_action: 'arrival_params_selected',
                event_label: '',
            })
        // analytics >
    }

    useEffect(() => {
        hasFamilyPackageSucceeded && doShowTicketsActions(true)
    }, [hasFamilyPackageSucceeded])

    useEffect(() => {
        dates.length &&
            isTablet &&
            scrollTo({ id: 'tickets-settings', duration: 1000 })
    }, [dates])

    useEffect(() => {
        animatePendingScroll('tickets-settings')
    }, [scrollIndex])

    useEffect(() => {
        // auto add number of guests from accommodation step (only in alternative creator path)
        if (!isStepEmpty('accommodation') && isStepEmpty('tickets')) {
            const ticketsAmountToAdd =
                accommodationGuestsCount !== people
                    ? accommodationGuestsCount - 1
                    : 0

            dispatch(setMultipleDays(true))
            Array.from({ length: ticketsAmountToAdd }).forEach(() =>
                dispatch(addPerson())
            )
        }

        return () => {
            if (isStepEmpty('tickets')) dispatch(removeAllTickets())
        }
    }, [])

    const setButtonWidth = useMemo(() => {
        if (language === 'pl') {
            if (isDisabledFamilyPackage) return '180px'
            if (isSpecialOfferUnselected) return 'initial'
        }

        return '180px'
    }, [language, isSpecialOfferUnselected, isDisabledFamilyPackage])

    return (
        <Section id="tickets-settings" theme={theme}>
            <Text
                variant="heading2"
                color="grayscale_900"
                mb={[24, 12]}
                theme={theme}
            >
                <FormattedMessage
                    id="tickets.config.section_caption"
                    defaultMessage="Znajdź bilety"
                />
            </Text>

            {renderActivePromotions({
                sectionType: 'tickets',
                key: 'initialInfo',
                component: <DefaultInfo />,
            })}

            {isCreatorAccommodation && <TicketsWithAccommodationDiscountInfo />}

            <>
                <Box align="stretch" mb={16}>
                    <Box direction={['column', 'row']} align="stretch" gap={32}>
                        <div>
                            <Text
                                variant="heading4"
                                color={labelColor}
                                mb={[12, 16]}
                            >
                                <FormattedMessage
                                    id="tickets.config.length_label"
                                    defaultMessage="Długość pobytu"
                                />
                            </Text>
                            <SwitchDays
                                isChecked={isMultipleDays}
                                handleClick={() => toggleMultipleDays()}
                                hasDarkMode={isDarkThemeForTickets}
                            />
                        </div>

                        <Box isFullWidth>
                            <Text
                                variant="heading4"
                                color={labelColor}
                                mb={[12, 16]}
                            >
                                <FormattedMessage
                                    id="tickets.config.date_label"
                                    defaultMessage="Data przyjazdu"
                                />
                            </Text>
                            <Datepicker
                                id="date-of-visit"
                                placeholder={formatMessage({
                                    id: 'global.input.choose_date_label',
                                    defaultMessage: 'Wybierz datę',
                                })}
                                value={dates}
                                openCalendar={() =>
                                    areTicketsUpdating
                                        ? null
                                        : dispatch(setIsCalendarOpened(true))
                                }
                                hasNoMaxWidth
                                testId="ticket-trigger-stay-date"
                                hasDarkMode={isDarkThemeForTickets}
                                ref={dateFieldRef}
                            />
                            {isMaintenanceInfoVisible && isTablet && (
                                <Box mt={8} isFullWidth>
                                    <MaintenanceBox
                                        variant="info"
                                        event={matchingEvent}
                                        onOpenHoursModal={() => {
                                            setIsDifferentHoursOpened(true)
                                        }}
                                        hasDarkMode={isDarkThemeForTickets}
                                    />
                                </Box>
                            )}
                        </Box>
                        <Box>
                            <Text
                                variant="heading4"
                                color={labelColor}
                                mb={[12, 16]}
                                hasNoWrap
                            >
                                <FormattedMessage
                                    id="global.input.people_quantity_label"
                                    defaultMessage="Liczba osób"
                                />
                            </Text>
                            <NumberPicker
                                minValue={
                                    isFamilyPackageSelected
                                        ? FAMILY_PACKAGE_PERSON_COUNT
                                        : 1
                                }
                                maxValue={TICKETS_TO_PARK_LIMIT}
                                value={people}
                                handleAdd={handleAddPerson}
                                handleSubtract={handleRemovePerson}
                                testIdPrefix="ticket"
                                theme={theme}
                            />
                        </Box>
                    </Box>
                    <Box mt={[16, 24]} isFullWidth>
                        {isMaintenanceInfoVisible && !isTablet && (
                            <Box mb={24} isFullWidth>
                                <MaintenanceBox
                                    variant="info"
                                    event={matchingEvent}
                                    onOpenHoursModal={() => {
                                        setIsDifferentHoursOpened(true)
                                    }}
                                    hasDarkMode={isDarkThemeForTickets}
                                />
                            </Box>
                        )}
                        {((isFamilyPackageVisible &&
                            reducedFamilyData &&
                            isFamilyDataComplete) ||
                            isFamilyPackageDataFetching) &&
                            !isDisabledFamilyPackage && (
                                <Box isFullWidth mt={[16, 0]} mb={24}>
                                    <Text
                                        variant="heading4"
                                        color={labelColor}
                                        mb={[12, 16]}
                                    >
                                        <FormattedMessage
                                            id="tickets.config.special_offer"
                                            defaultMessage="Oferta specjalna"
                                        />
                                    </Text>
                                    {isFamilyPackageDataFetching ||
                                    !reducedFamilyData ? (
                                        <FamilyLoader
                                            hasDarkMode={isDarkThemeForTickets}
                                        />
                                    ) : (
                                        <FamilyCheckbox
                                            bundleData={reducedFamilyData}
                                            isChecked={isFamilyPackageSelected}
                                            handleCheckboxChange={
                                                handleCheckboxChangeWithEffects
                                            }
                                            hasPriceHidden={!dates.length}
                                        />
                                    )}
                                </Box>
                            )}
                    </Box>
                </Box>
                <Button
                    variant={isDarkThemeForTickets ? 'primary' : 'secondary'}
                    isDisabled={isContinueDisabled}
                    isLoading={
                        isFamilyPackageProcessing ||
                        isTicketProcessing ||
                        isFamilyPackageDataFetching ||
                        areTicketsUpdating ||
                        isCartFetching
                    }
                    onClick={handleShowTickets}
                    width={['100%', setButtonWidth]}
                    testId="ticket-button-continue"
                >
                    {isSpecialOfferUnselected &&
                    !isDisabledFamilyPackage &&
                    isFamilyDataComplete ? (
                        <FormattedMessage
                            id="package.family.continue_without"
                            defaultMessage="Kontynuuj bez pakietu"
                        />
                    ) : (
                        <FormattedMessage
                            id="global.btn.continue"
                            defaultMessage="Kontynuuj"
                        />
                    )}
                </Button>
            </>
            <CalendarModal
                isOpened={isCalendarOpened}
                close={() => {
                    dispatch(setIsCalendarOpened(false))
                    dateFieldRef?.current?.focus()
                }}
                openHoursModal={() => {
                    setIsDifferentHoursOpened(true)
                    dispatch(setIsCalendarOpened(false))
                }}
            />
            <OpeningHoursModal
                isOpened={isDifferentHoursOpened}
                close={() => setIsDifferentHoursOpened(false)}
            />
        </Section>
    )
}

export default FindTickets
